/**
 * StatusIndicator — one compact way to show a status: an icon plus a short label.
 * =================================================================================
 * Replaces the filled, coloured antd Tags on the genealogy screens. Colour is semantic and
 * sparing: it is carried by the icon only, taken from the active antd theme tokens (so it follows
 * light / dark), and the label text stays in the normal text colour. Neutral information
 * ("none recorded", "unfrozen") is muted grey, never a warning.
 *
 * The icon is never the only carrier of meaning: the label is always rendered, and when a caller
 * passes no label the icon gets an aria-label instead.
 *
 * The client does not decide a state. Callers map the server's own status words to a `state`
 * (the same mapping the old Tag colours used) and pass the server's wording as the `label`.
 *
 * Also exports:
 *   - StatusNotice — a quiet bordered panel with an indicator icon in the heading, used in place of
 *     the full-width orange / red antd Alerts.
 *   - stateForStatus / stateForCleaning — the shared status-word → state maps.
 */
import React from 'react';
import { theme, Tooltip } from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';

/** state → icon + antd token for the icon colour + whether the label is muted. */
const STATES = {
  ok: { Icon: CheckCircleFilled, token: 'colorSuccess', word: 'OK' },
  warning: { Icon: ExclamationCircleFilled, token: 'colorWarning', word: 'Warning' },
  error: { Icon: CloseCircleFilled, token: 'colorError', word: 'Failed' },
  unknown: { Icon: QuestionCircleOutlined, token: 'colorTextTertiary', muted: true, word: 'Unknown' },
  hold: { Icon: ClockCircleOutlined, token: 'colorWarning', word: 'On hold' },
  pending: { Icon: ClockCircleOutlined, token: 'colorTextTertiary', word: 'Pending' },
  quarantine: { Icon: StopOutlined, token: 'colorWarning', word: 'Quarantine' },
  active: { Icon: SyncOutlined, token: 'colorInfo', word: 'In progress' },
  info: { Icon: InfoCircleFilled, token: 'colorInfo', word: 'Information' },
  neutral: { Icon: MinusCircleOutlined, token: 'colorTextTertiary', muted: true, word: 'None' },
};

/**
 * Server status word → state. Mirrors the colour map the genealogy page already used
 * (green → ok, blue → active, orange → hold / quarantine, red → error, default → neutral).
 */
const STATUS_STATE = {
  Completed: 'ok',
  Running: 'active',
  Hold: 'hold',
  Pending: 'pending',
  released: 'ok',
  approved: 'ok',
  quarantine: 'quarantine',
  rejected: 'error',
};
export const stateForStatus = (s) => STATUS_STATE[s] || 'neutral';

/** Equipment cleaning_status as the server words it: APPROVED · "no record" · anything else. */
export const stateForCleaning = (v) => {
  if (!v) return 'neutral';
  if (v === 'APPROVED') return 'ok';
  if (v === 'no record') return 'error';
  return 'warning';
};

const StatusIndicator = ({ state = 'neutral', label, tooltip, ariaLabel, strong = false, className, style }) => {
  const { token } = theme.useToken();
  const cfg = STATES[state] || STATES.neutral;
  const { Icon } = cfg;
  const hasLabel = label !== undefined && label !== null && label !== '';
  const body = (
    <span
      className={`gen-status gen-status--${state}${className ? ` ${className}` : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        lineHeight: 1.4,
        verticalAlign: 'middle',
        marginInlineEnd: 10,
        color: cfg.muted ? token.colorTextSecondary : token.colorText,
        fontWeight: strong ? 600 : undefined,
        ...style,
      }}
    >
      <Icon
        style={{ color: token[cfg.token], fontSize: '1em' }}
        role={hasLabel ? undefined : 'img'}
        aria-label={hasLabel ? undefined : ariaLabel || cfg.word}
        aria-hidden={hasLabel ? true : undefined}
      />
      {hasLabel ? <span>{label}</span> : null}
    </span>
  );
  return tooltip ? <Tooltip title={tooltip}>{body}</Tooltip> : body;
};

/**
 * StatusNotice — a quiet panel (theme border + subtle fill) whose heading carries the indicator
 * icon. Use in place of a full-width coloured Alert so the colour sits in the icon only.
 */
export const StatusNotice = ({ state = 'warning', title, children, className, style }) => {
  const { token } = theme.useToken();
  const cfg = STATES[state] || STATES.neutral;
  const { Icon } = cfg;
  return (
    <div
      role={state === 'error' || state === 'warning' ? 'alert' : 'status'}
      className={`gen-notice${className ? ` ${className}` : ''}`}
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorFillAlter,
        borderRadius: token.borderRadiusLG,
        padding: '10px 14px',
        color: token.colorText,
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
        <Icon style={{ color: token[cfg.token], fontSize: 16 }} aria-hidden />
        <span>{title}</span>
      </div>
      {children ? <div style={{ marginTop: 6, paddingLeft: 24 }}>{children}</div> : null}
    </div>
  );
};

export default StatusIndicator;
